/**
 * DonutChart — reusable SVG stroke-dasharray donut.
 *
 * Props:
 *   segments  – [{label, value, color}] — sorted descending by value before render.
 *   centerTitle – small text in the center (e.g. "Total")
 *   centerValue – larger text in the center (e.g. "R$ 1.200")
 *   size        – diameter in px (default 200)
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface DonutSegment {
    label: string;
    value: number;
    color: string;
}

interface DonutChartProps {
    segments: DonutSegment[];
    centerTitle?: string;
    centerValue?: string;
    size?: number;
}

/** Consistent 10-hue categorical palette (brand-aware, accessible). */
export const DONUT_PALETTE: string[] = [
    '#7266e8', // gs-primary purple
    '#34c38f', // gs-success green
    '#f46a6a', // gs-danger coral
    '#50c5e0', // sky blue
    '#f9a825', // amber
    '#8d6e63', // warm brown
    '#ab47bc', // lavender
    '#26a69a', // teal
    '#ef5350', // red
    '#42a5f5', // blue
];

const DonutChart: React.FC<DonutChartProps> = ({
    segments,
    centerTitle,
    centerValue,
    size = 200,
}) => {
    const { t } = useTranslation();
    const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

    const radius = (size / 2) * 0.72;
    const cx = size / 2;
    const cy = size / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeWidth = size * 0.115;

    const total = segments.reduce((s, seg) => s + seg.value, 0);

    if (total === 0) {
        return (
            <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width={size} height={size}>
                    <circle
                        cx={cx}
                        cy={cy}
                        r={radius}
                        fill="none"
                        stroke="var(--gs-border)"
                        strokeWidth={strokeWidth}
                    />
                    <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                        fill="var(--gs-text-muted)" fontSize={size * 0.07} fontWeight={500}>
                        {t('common.noData')}
                    </text>
                </svg>
            </div>
        );
    }

    // Build dash arrays — rotate so first segment starts at top (−90°)
    let offset = circumference * 0.25; // 25% = 90° → starts at top
    const slices = segments.map((seg, i) => {
        const pct = seg.value / total;
        const dash = pct * circumference;
        const gap = circumference - dash;
        const strokeDashoffset = circumference - offset + circumference;
        const startOffset = offset;
        offset += dash;
        return { ...seg, dash, gap, strokeDashoffset: circumference - startOffset, index: i };
    });

    const activeSlice = hoveredIdx !== null ? slices[hoveredIdx] : null;

    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={{ display: 'block', overflow: 'visible' }}
            data-cy="donut-chart"
        >
            {/* Background ring */}
            <circle
                cx={cx} cy={cy} r={radius}
                fill="none"
                stroke="var(--gs-border)"
                strokeWidth={strokeWidth}
            />

            {/* Segments */}
            {slices.map((sl, i) => {
                const isHovered = hoveredIdx === i;
                const scale = isHovered ? 1.04 : 1;
                return (
                    <circle
                        key={sl.label + i}
                        cx={cx} cy={cy} r={radius}
                        fill="none"
                        stroke={sl.color}
                        strokeWidth={isHovered ? strokeWidth * 1.18 : strokeWidth}
                        strokeDasharray={`${sl.dash} ${sl.gap}`}
                        strokeDashoffset={sl.strokeDashoffset}
                        strokeLinecap="round"
                        style={{
                            transition: 'stroke-width 0.18s ease, transform 0.18s ease',
                            transformOrigin: `${cx}px ${cy}px`,
                            transform: `scale(${scale})`,
                            cursor: 'pointer',
                        }}
                        onMouseEnter={() => setHoveredIdx(i)}
                        onMouseLeave={() => setHoveredIdx(null)}
                        data-cy={`donut-segment-${i}`}
                    >
                        <title>{sl.label}: {(sl.value / total * 100).toFixed(1)}%</title>
                    </circle>
                );
            })}

            {/* Center text */}
            {activeSlice ? (
                <>
                    <text x={cx} y={cy - size * 0.055} textAnchor="middle"
                        fill={activeSlice.color} fontSize={size * 0.065} fontWeight={700}>
                        {(activeSlice.value / total * 100).toFixed(1)}%
                    </text>
                    <text x={cx} y={cy + size * 0.065} textAnchor="middle"
                        fill="var(--gs-text-muted)" fontSize={size * 0.058} fontWeight={500}>
                        {activeSlice.label.length > 12 ? activeSlice.label.slice(0, 11) + '…' : activeSlice.label}
                    </text>
                </>
            ) : (
                <>
                    {centerTitle && (
                        <text x={cx} y={cy - size * 0.06} textAnchor="middle"
                            fill="var(--gs-text-muted)" fontSize={size * 0.062} fontWeight={600}>
                            {centerTitle}
                        </text>
                    )}
                    {centerValue && (
                        <text x={cx} y={cy + (centerTitle ? size * 0.065 : 0)} textAnchor="middle"
                            fill="var(--gs-text)" fontSize={size * 0.072} fontWeight={800}>
                            {centerValue.length > 10 ? centerValue.slice(0, 9) + '…' : centerValue}
                        </text>
                    )}
                </>
            )}
        </svg>
    );
};

export default DonutChart;
